import { Avatar, Flex, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState, useContext, useMemo, Fragment } from 'react';
import { ChatBubble } from '@duke-hq/ui/components/Chat/ChatBubble';
import type { ChatEvent } from '@duke-hq/svc-support/trpc/clients/web';
import { ChatContext } from './Context';
import { useDebounceCallback } from 'usehooks-ts';
import { useChatEvents } from './useChatEvents';
import { djs, type Dayjs } from '@duke-hq/djs';

function formatDate(date: string | number | Date | Dayjs | null | undefined) {
  if (date == null) {
    return '';
  }
  const dayjsObject = djs(date);
  if (dayjsObject.isSame(djs(), 'day')) {
    return 'Today';
  } else if (dayjsObject.isSame(djs().subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  } else {
    return dayjsObject.format('dddd, D MMMM');
  }
}

export function Messages({
  chatId,
  currentUserDrn,
  onMessageReceived,
}: {
  chatId: string;
  currentUserDrn: string;
  onMessageReceived?: () => void;
}) {
  return (
    <Flex alignItems="flex-end" alignSelf="stretch" width="100%" flex={1} minHeight="0">
      {chatId ? (
        <MessagesForChat
          key={chatId}
          chatId={chatId}
          currentUserDrn={currentUserDrn}
          onMessageReceived={onMessageReceived}
        />
      ) : (
        <EmptyState />
      )}
    </Flex>
  );
}

function EmptyState() {
  return <p>Start a new chat</p>;
}

function MessagesForChat({
  chatId,
  currentUserDrn,
  onMessageReceived,
}: {
  chatId: string;
  currentUserDrn: string;
  onMessageReceived?: () => void;
}) {
  const [currentlyTyping, _setCurrentlyTyping] = useState<Record<string, boolean>>({});
  const [hasScrolledOnLoad, setHasScrolledOnLoad] = useState(false);
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const {
    events: eventsOrdered,
    initialPage,
    loadEarlier: loadMoreMessages,
    isLoadingEarlier: isLoadingPage,
  } = useChatEvents({ chatId, currentUserDrn, onMessageReceived });

  // Scroll to bottom on initial load
  useEffect(() => {
    if (initialPage?.events === undefined) {
      return;
    }

    const id = requestAnimationFrame(() => {
      const node = scrollTargetRef.current;

      if (node !== null) {
        node.scrollTop = node.scrollHeight;
        setHasScrolledOnLoad(true);
      }
    });

    return () => cancelAnimationFrame(id);
  }, [initialPage]);

  const handleScroll = useCallback(() => {
    const node = scrollTargetRef.current;

    if (node === null || !hasScrolledOnLoad) {
      return;
    }

    if (node.scrollTop < 200) {
      void loadMoreMessages();
    }
  }, [loadMoreMessages, hasScrolledOnLoad]);

  useEffect(() => {
    const node = scrollTargetRef.current;

    if (node === null) {
      return;
    }

    node.addEventListener('scroll', handleScroll);

    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // TODO Send Message behaviour
  //
  // When we send a message, add it to messages with a flag
  // indicating it's an optimistic update. when the event
  // comes back from the server, replace the message with the
  // real one.
  //
  // This means if the user has 2 windows open, they will always
  // see the same messages, even if they send a message from
  // one window and then refresh the other.

  // TODO: the list of events will need to be virtualised at some point, as a long
  //    chat history will be slow to render.

  // When the user is near the bottom/ at the bottom of the panel, enable auto scroll
  // so new messages scroll into view automatically but if the user has scrolled
  // up to read previous messages, don't auto scroll.
  const isUserNearBottom = useCallback(() => {
    const node = scrollTargetRef.current;

    if (node === null) {
      return false;
    }

    const distanceToBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    return distanceToBottom < 200;
  }, []);

  const scrollToBottom = useDebounceCallback(
    useCallback(() => {
      if (isLoadingPage.current || scrollTargetRef.current == null) {
        return;
      }

      if (scrollTargetRef.current) {
        scrollTargetRef.current.scrollTo({
          behavior: 'smooth',
          top: scrollTargetRef.current.scrollHeight + scrollTargetRef.current.offsetHeight,
        });
      }
    }, [scrollTargetRef, isLoadingPage]),
    hasScrolledOnLoad ? 30 : 500,
  );

  // Scroll on new events if user is near bottom
  useEffect(() => {
    if (!hasScrolledOnLoad || isLoadingPage.current) {
      return;
    }

    if (isUserNearBottom()) {
      scrollToBottom();
    }
  }, [eventsOrdered, hasScrolledOnLoad, isUserNearBottom, scrollToBottom, isLoadingPage]);

  return (
    <Stack
      bg="bg.surface"
      maxHeight="100%"
      minHeight="0"
      flex={1}
      justify="flex-end"
      overflow="hidden"
    >
      <ChatEventList
        events={eventsOrdered}
        currentUserDrn={currentUserDrn}
        scrollTargetRef={scrollTargetRef}
      />
      <TypingIndicator currentlyTyping={currentlyTyping} userDrn={currentUserDrn} />
    </Stack>
  );
}

function ChatEventList({
  events,
  currentUserDrn,
  scrollTargetRef,
}: {
  events: ChatEvent[];
  currentUserDrn: string;
  scrollTargetRef: React.RefObject<HTMLDivElement | null>;
}) {
  let lastUserDrn: string | null = null;
  let lastDate: string | null = null;

  return (
    <Stack
      width="100%"
      alignSelf="flex-end"
      flex={1}
      overflow="auto"
      p="4"
      ref={scrollTargetRef}
      gap="1"
    >
      {events.map((evt, index) => {
        const isNewGroup = evt.userDrn !== lastUserDrn;
        if (isNewGroup) {
          lastUserDrn = evt.userDrn;
        }
        const isLastInGroup =
          (events && index === events.length - 1) || events[index + 1]?.userDrn !== evt.userDrn;
        const eventDate = formatDate(evt.created);
        let showDateSeparator = false;
        if (eventDate !== '') {
          showDateSeparator = eventDate !== lastDate;
          lastDate = eventDate;
        }
        const previousEvent = index > 0 ? events[index - 1] : null;
        const nextEvent = index < events.length - 1 ? events[index + 1] : null;

        return (
          <Fragment key={`chat-event#${evt.id}`}>
            {showDateSeparator && (
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="fg.muted"
                textAlign="center"
                mt="2"
                mb="1"
              >
                {eventDate}
              </Text>
            )}
            <RenderChatEvent
              key={`chat-event#${evt.id}`}
              evt={evt}
              currentUserDrn={currentUserDrn}
              isNewGroup={isNewGroup}
              isLastInGroup={isLastInGroup}
              previousEvent={previousEvent}
              nextEvent={nextEvent}
            />
          </Fragment>
        );
      })}
    </Stack>
  );
}

function TypingIndicator({
  userDrn,
  currentlyTyping,
}: {
  userDrn: string;
  currentlyTyping: Record<string, boolean>;
}) {
  const typingUsers = Object.keys(currentlyTyping).filter((id) => id !== userDrn);
  if (typingUsers.length === 0) {
    return null;
  }

  // TODO: swap this for names
  const typingNames = typingUsers.join(', ');
  const quantityWord = typingUsers.length > 1 ? 'are' : 'is';

  return (
    typingUsers.length > 0 && (
      <Text fontSize="sm" color="gray.500">
        {typingNames} {quantityWord} typing...
      </Text>
    )
  );
}

function RenderChatEvent({
  evt,
  currentUserDrn,
  isNewGroup,
  isLastInGroup,
  previousEvent,
  nextEvent,
}: {
  evt: ChatEvent;
  currentUserDrn: string;
  isNewGroup: boolean;
  isLastInGroup: boolean;
  previousEvent: ChatEvent | null | undefined;
  nextEvent: ChatEvent | null | undefined;
}) {
  const ctx = useContext(ChatContext);

  const user = useMemo(() => {
    return ctx.getUser(evt.userDrn);
  }, [evt.userDrn, ctx]);

  const [showTimeForEvent, setShowTimeForEvent] = useState<Record<string, boolean>>({});

  let formattedTime = '';
  if (evt.created) {
    const dateObject = new Date(evt.created);
    const dayjsObject = djs(dateObject);
    formattedTime = dayjsObject.format('hh:mm A');
  }

  // Calculate whether to show the time for each chat event
  // Show time if:
  // 1. It has been toggled on by the user.
  // 2. It is the last message in a group.
  // 3. There is a gap of more than 10 minutes between the current message
  //    and the previous message, and the next message is from a different user.
  // 4. It is the first message of a new day
  //    (nextEvent is null or the next event's date is different from the current event's date).
  const showTime = useMemo(() => {
    if (showTimeForEvent[evt.id]) {
      return true;
    }

    if (isLastInGroup) {
      return true;
    }

    if (previousEvent) {
      const prevTime = djs(previousEvent.created);
      const currTime = djs(evt.created);
      const timeDiff = currTime.diff(prevTime, 'minute');

      if (timeDiff > 10 && nextEvent?.userDrn !== evt.userDrn) {
        return true;
      }
    }

    if (!nextEvent || djs(nextEvent.created).isAfter(djs(evt.created), 'day')) {
      return true;
    }

    return false;
  }, [isLastInGroup, previousEvent, nextEvent, evt.created, showTimeForEvent]);

  const toggleShowMetaData = useCallback(() => {
    setShowTimeForEvent((prev) => ({
      ...prev,
      [evt.id]: !prev[evt.id],
    }));
  }, [evt.id]);

  switch (evt.ty) {
    case 'user.isTyping':
      return null;
    case 'EventMessage':
      if (evt.userDrn === currentUserDrn) {
        return (
          <ChatBubble
            key={`EventMessage#${evt.id}`}
            body={evt.text}
            time={showTime ? formattedTime : undefined}
            isCurrentUser={evt.userDrn === currentUserDrn}
            onClick={toggleShowMetaData}
          />
        );
      } else {
        return (
          <Fragment key={`EventMessage#${evt.id}`}>
            {isNewGroup ? (
              <Flex mt="1">
                {user && user?.displayName != null ? (
                  <Flex alignItems="center" gap="2">
                    <div style={{ width: '2rem' }}></div>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="fg.muted"
                      m="0"
                      p="0"
                      textAlign={evt.userDrn === currentUserDrn ? 'right' : 'left'}
                    >
                      {user.displayName}
                    </Text>
                  </Flex>
                ) : null}
              </Flex>
            ) : null}
            <Flex alignItems="flex-end" gap="2">
              {isLastInGroup ? (
                <Avatar
                  size="sm"
                  name={user?.displayName ?? undefined}
                  src={user?.avatar ?? undefined}
                  flexShrink={0}
                />
              ) : (
                <div style={{ width: '2rem' }}></div>
              )}
              <ChatBubble
                body={evt.text}
                time={showTime ? formattedTime : undefined}
                isCurrentUser={evt.userDrn === currentUserDrn}
                isLastInGroup={isLastInGroup}
                onClick={toggleShowMetaData}
              />
            </Flex>
          </Fragment>
        );
      }
    // return (
    //   <>
    //     {user && user?.displayName && (
    //       <Text
    //         key={evt.id}
    //         fontSize="sm"
    //         color="gray.500"
    //         m="0"
    //         p="0"
    //         textAlign={evt.userDrn === currentUserDrn ? 'right' : 'left'}
    //       >
    //         {user.displayName}
    //       </Text>
    //     )}
    //     <ChatBubble
    //       key={evt.id}
    //       body={evt.text}
    //       // time={formattedTime}
    //       isCurrentUser={evt.userDrn === currentUserDrn}
    //     />
    //   </>
    // );

    case 'EventUserJoined':
      return (
        <Text key={`EventUserJoined#${evt.id}`} fontSize="sm" color="gray.500" textAlign="center">
          {typeof user?.displayName === 'string' ? `${user.displayName} joined the chat.` : ''}
        </Text>
      );

    case 'EventUserLeft':
      return (
        <Text key={`EventUserLeft#${evt.id}`} fontSize="sm" color="gray.500" textAlign="center">
          {typeof user?.displayName === 'string' ? `${user.displayName} left the chat.` : ''}
        </Text>
      );

    case 'EventChatClosed':
      return (
        <Text key={`EventChatClosed#${evt.id}`} fontSize="sm" color="gray.500" textAlign="center">
          {evt.systemInitiated === true
            ? 'This chat was closed.'
            : typeof user?.displayName === 'string'
              ? `${user.displayName} closed the chat.`
              : 'Someone closed the chat.'}
        </Text>
      );
    case 'EventChatOpened':
      return (
        <Text key={`EventChatOpened#${evt.id}`} fontSize="sm" color="gray.500" textAlign="center">
          {typeof user?.displayName === 'string'
            ? `${user.displayName} reopened the chat.`
            : 'Someone reopened the chat.'}
        </Text>
      );

    default:
      return null;
  }
}
